/**
 * All available permissions in the system.
 * Format: `module` for view access, `module_mutate` for create/edit/delete.
 */
export const PERMISSIONS = [
  // Admin members management
  "members",
  "members_mutate",
  // Roles management
  "roles",
  "roles_mutate",
  // Children/students
  "children",
  "children_mutate",
  // Nominations
  "nominations",
  "nominations_mutate",
  // Sponsors/donors
  "sponsors",
  "sponsors_mutate",
  // Giving/donations
  "giving",
  "giving_mutate",
  // Partner schools
  "schools",
  "schools_mutate",
  // Programs
  "programs",
  "programs_mutate",
  // Ability Program
  "ability",
  "ability_mutate",
  // Events
  "events",
  "events_mutate",
  // Forms (inquiries, applications)
  "forms",
  "forms_mutate",
  // Content (newsletters, etc.)
  "content",
  "content_mutate",
] as const

export type Permission = (typeof PERMISSIONS)[number]

/** Group permissions by module for UI display */
export const PERMISSION_GROUPS = {
  "Team Management": ["members", "members_mutate", "roles", "roles_mutate"],
  Children: ["children", "children_mutate"],
  Nominations: ["nominations", "nominations_mutate"],
  Sponsors: ["sponsors", "sponsors_mutate"],
  Giving: ["giving", "giving_mutate"],
  Schools: ["schools", "schools_mutate"],
  Programs: ["programs", "programs_mutate"],
  "Ability Program": ["ability", "ability_mutate"],
  Events: ["events", "events_mutate"],
  Forms: ["forms", "forms_mutate"],
  Content: ["content", "content_mutate"],
} as const satisfies Record<string, readonly Permission[]>

/** Human-readable labels for permissions */
export const PERMISSION_LABELS: Record<Permission, string> = {
  members: "View members",
  members_mutate: "Manage members",
  roles: "View roles",
  roles_mutate: "Manage roles",
  children: "View children",
  children_mutate: "Manage children",
  nominations: "View nominations",
  nominations_mutate: "Manage nominations",
  sponsors: "View sponsors",
  sponsors_mutate: "Manage sponsors",
  giving: "View giving",
  giving_mutate: "Manage giving",
  schools: "View schools",
  schools_mutate: "Manage schools",
  programs: "View programs",
  programs_mutate: "Manage programs",
  ability: "View ability beneficiaries",
  ability_mutate: "Manage ability beneficiaries",
  events: "View events",
  events_mutate: "Manage events",
  forms: "View forms",
  forms_mutate: "Manage forms",
  content: "View content",
  content_mutate: "Manage content",
}

/** Check if a permission set includes a specific permission */
export function hasPermission(permissions: string[], required: Permission): boolean {
  return permissions.includes(required)
}

/** Check if a permission set includes any of the required permissions */
export function hasAnyPermission(permissions: string[], required: Permission[]): boolean {
  return required.some((p) => permissions.includes(p))
}

/** Check if a permission set includes all of the required permissions */
export function hasAllPermissions(permissions: string[], required: Permission[]): boolean {
  return required.every((p) => permissions.includes(p))
}
